import {
  getDoc,
  getDocFromCache,
  getDocs,
  getDocsFromCache,
  onSnapshot,
  type DocumentData,
  type DocumentReference,
  type Query,
  type QueryDocumentSnapshot,
  type Unsubscribe,
} from 'firebase/firestore'

/** Read a query from local cache when available, then refresh from the server. */
export async function getQueryDocsCacheFirst(
  q: Query<DocumentData>
): Promise<QueryDocumentSnapshot<DocumentData>[]> {
  try {
    const cached = await getDocsFromCache(q)
    if (!cached.empty) {
      void getDocs(q).catch(() => {})
      return cached.docs
    }
  } catch {
    // Cache miss — fall through to network.
  }

  const snap = await getDocs(q)
  return snap.docs
}

/** Read a document from local cache when available, then refresh from the server. */
export async function getDocumentCacheFirst(
  ref: DocumentReference<DocumentData>
) {
  try {
    const cached = await getDocFromCache(ref)
    if (cached.exists()) {
      void getDoc(ref).catch(() => {})
      return cached
    }
  } catch {
    // Cache miss — fall through to network.
  }

  return getDoc(ref)
}

/**
 * Live query with offline persistence — emits cached results immediately, then server updates.
 */
export function subscribeQuery<T>(
  q: Query<DocumentData>,
  map: (docs: QueryDocumentSnapshot<DocumentData>[]) => T,
  onData: (value: T) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  return onSnapshot(
    q,
    (snap) => {
      onData(map(snap.docs))
    },
    (err) => {
      onError?.(err instanceof Error ? err : new Error(String(err)))
    }
  )
}
